import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Breadcrumbs, Typography, Box } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const Breadcrumb = ({ customItems = null, productName = null }) => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  // Generate breadcrumb items
  const generateBreadcrumbItems = () => {
    if (customItems) {
      return customItems;
    }

    const items = [
      { label: 'Hem', href: '/' }
    ];

    // Add path-based items
    let currentPath = '';
    pathnames.forEach((pathname, index) => {
      currentPath += `/${pathname}`;
      
      if (pathname === 'products' && pathnames[index + 1]) {
        // Product detail page
        items.push({
          label: 'Produkter',
          href: '/'
        });
        if (productName) {
          items.push({
            label: productName,
            href: currentPath + `/${pathnames[index + 1]}`,
            isLast: true
          });
        }
      } else if (pathname === 'category') {
        items.push({
          label: 'Kategorier',
          href: currentPath,
          isLast: index === pathnames.length - 1
        });
      } else if (pathname === 'search') {
        items.push({
          label: 'Sök',
          href: currentPath,
          isLast: true
        });
      }
    });

    return items;
  };

  const breadcrumbItems = generateBreadcrumbItems();

  // Generate JSON-LD for breadcrumb
  const generateBreadcrumbJsonLd = () => {
    const itemListElement = breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `https://hittadeals.com${item.href}` : undefined
    }));

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement
    };
  };

  if (breadcrumbItems.length <= 1) {
    return null; // Don't show breadcrumb for homepage
  }

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(generateBreadcrumbJsonLd())}
        </script>
      </Helmet>
      
      <Box sx={{ mb: 2, mt: 1 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{
            '& .MuiBreadcrumbs-separator': {
              color: 'text.secondary'
            }
          }}
        >
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1 || item.isLast;
            
            if (isLast) {
              return (
                <Typography
                  key={index}
                  color="text.primary"
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}
                >
                  {item.label}
                </Typography>
              );
            }
            
            return (
              <Link
                key={index}
                to={item.href}
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  fontSize: '0.875rem'
                }}
              >
                <Typography
                  color="text.secondary"
                  sx={{
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline'
                    },
                    fontSize: '0.875rem'
                  }}
                >
                  {item.label}
                </Typography>
              </Link>
            );
          })}
        </Breadcrumbs>
      </Box>
    </>
  );
};

export default Breadcrumb;